import { useCallback } from 'react';

import {
  useChangeAttendanceMutation,
  ChangeAttendanceMutationVariables,
} from 'core/graphql';
import { useParseServerErrors } from 'services/ErrorsService';

type UpdateAttendanceType = [
  (agreementInput: ChangeAttendanceMutationVariables) => void,
  {
    success: boolean;
    loading: boolean;
  },
];

/**
 * Правка отметки посещаемости из сетки журнала.
 *
 * ТЗ 0026: ручной `cache.modify` убран. Мутация всегда возвращала
 * `AttendanceType` — фронт его просто не запрашивал и вместо этого писал в
 * кэш сам, а при неуспехе ИНВЕРТИРОВАЛ значение обратно. Инверсия — не
 * откат: она угадывает прежнее состояние, и на строке, которая была «не
 * отмечена» (null), давала ложное «отсутствовал». С приходом `source` цена
 * такой догадки выросла бы ещё: локально выставленное значение не имеет
 * источника вовсе.
 *
 * Теперь сущность выбирается в самом документе, Apollo нормализует её по id и
 * сам откатывает оптимистику при ошибке.
 */
export const useUpdateAttendance = (): UpdateAttendanceType => {
  const [updateAttendance, { data, loading }] = useChangeAttendanceMutation({
    onError: (err) => err,
  });

  useParseServerErrors(data?.updateAttendance?.customErrors);

  const changeAttendance = useCallback(
    (variables: ChangeAttendanceMutationVariables) =>
      updateAttendance({
        variables,
        optimisticResponse: {
          updateAttendance: {
            __typename: 'UpdateAttendanceMutation',
            success: true,
            customErrors: null,
            attendance: {
              __typename: 'AttendanceType',
              id: String(variables.attendanceId),
              attendance: variables.attendance,
              source: 'manual',
              markedAt: new Date().toISOString(),
              markedByName: null,
            },
          },
        },
      }),
    [updateAttendance],
  );

  return [
    changeAttendance,
    {
      success: Boolean(data?.updateAttendance?.success),
      loading,
    },
  ];
};
